import {initializeApp} from 'firebase/app'
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging'
import {firebaseConfig} from '../firebase.config'
import {toast} from 'react-toastify'

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Global callback for incoming calls - will be set by the IncomingCallProvider
let globalIncomingCallHandler: ((callData: any) => void) | null = null
// Global callback to close modal when call is accepted by another interpreter
let globalCloseModalHandler: ((callId: string, callHeaderId?: string) => void) | null = null

export const setIncomingCallHandler = (handler: (callData: any) => void) => {
  globalIncomingCallHandler = handler
}

/** Set by IncomingCallProvider — only the Connect master tab should handle incoming-call FCM. */
let globalIncomingCallMasterGuard: (() => boolean) | null = null

const INCOMING_CALL_TTL_MS = 20000
const openIncomingCallNotifications = new Map<string, Notification>()

function closeIncomingCallNotification(callId: string) {
  const notification = openIncomingCallNotifications.get(callId)
  if (!notification) return
  openIncomingCallNotifications.delete(callId)
  notification.close()
}

function isStaleIncomingCall(timestamp?: string) {
  const sentAt = Date.parse(timestamp || '')
  return Number.isFinite(sentAt) && Date.now() - sentAt > INCOMING_CALL_TTL_MS
}

export const setIncomingCallMasterGuard = (guard: (() => boolean) | null) => {
  globalIncomingCallMasterGuard = guard
}

function isIncomingCallMasterTab(): boolean {
  return globalIncomingCallMasterGuard?.() === true
}

export const setCloseModalHandler = (handler: (callId: string, callHeaderId?: string) => void) => {
  globalCloseModalHandler = handler
}

export class FirebaseMessagingService {
  private static instance: FirebaseMessagingService
  private currentToken: string | null = null
  private messaging: Messaging | null = null
  private messagingUnavailable = false

  private constructor() {}

  private async resolveMessaging(): Promise<Messaging | null> {
    if (this.messaging) return this.messaging
    if (this.messagingUnavailable) return null
    try {
      if (!(await isSupported())) {
        this.messagingUnavailable = true
        return null
      }
      this.messaging = getMessaging(app)
      return this.messaging
    } catch {
      this.messagingUnavailable = true
      return null
    }
  }

  public static getInstance(): FirebaseMessagingService {
    if (!FirebaseMessagingService.instance) {
      FirebaseMessagingService.instance = new FirebaseMessagingService()
    }
    return FirebaseMessagingService.instance
  }

  /**
   * Request notification permission and get FCM token
   */
  public async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      const messaging = await this.resolveMessaging()
      if (!messaging) return null

      if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
        return null
      }

      const permission = await window.Notification.requestPermission()

      if (permission === 'granted') {
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        })

        if (token) {
          this.currentToken = token
          return token
        } else {
          return null
        }
      } else {
        return null
      }
    } catch (error) {
      return null
    }
  }

  /**
   * Get current FCM token without requesting permission (if already granted)
   */
  public async getCurrentToken(): Promise<string | null> {
    try {
      const messaging = await this.resolveMessaging()
      if (!messaging) return null

      if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
        return null
      }

      if (window.Notification.permission === 'granted') {
        const registration = await this.ensureServiceWorkerRegistration()
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          ...(registration ? {serviceWorkerRegistration: registration} : {}),
        })

        if (token) {
          this.currentToken = token
          return token
        }
      }
      return null
    } catch (error) {
      return null
    }
  }

  /**
   * Ensure a healthy service worker registration exists, re-registering if it's
   * missing or inactive. Without this, only the login-time `initialize()` path
   * ever calls `registerServiceWorker()` — if the browser evicts/corrupts the SW
   * mid-session (known behavior under idle periods, browser updates, storage
   * pressure), the periodic refresh path could never detect or fix it, leaving
   * push notifications dead until a full re-login.
   */
  private async ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) return null
    try {
      const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
      if (existing && existing.active) return existing
    } catch {
      // fall through to re-register
    }
    return this.registerServiceWorker()
  }

  /**
   * Set up foreground message listener
   */
  public async setupForegroundMessageListener(): Promise<void> {
    const messaging = await this.resolveMessaging()
    if (!messaging) return

    onMessage(messaging, (payload: MessagePayload) => {
      // Check if this is an incoming call notification
      const action = payload.data?.action

      if (action === 'call_accepted') {
        const callId = payload.data?.callId || ''
        const callHeaderId = payload.data?.callHeaderId || ''
        closeIncomingCallNotification(callId)
        closeIncomingCallNotification(callHeaderId)
        
        // Close modal and stop ringing if handler is available
        if (globalCloseModalHandler) {
          console.log('✅ Closing modal and stopping ringtone via globalCloseModalHandler')
          try {
            globalCloseModalHandler(callId, callHeaderId)
            console.log('✅ Successfully called close modal handler')
          } catch (error) {
            console.error('❌ Error calling close modal handler:', error)
          }
        }
        
        // Show toast notification
        const title = payload.notification?.title || 'Call No Longer Available'
        const body = payload.notification?.body || payload.data?.message || 'This call has been accepted by another interpreter'
        
        toast.warning(`${title}: ${body}`, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      } else if (action === 'incoming-call') {
        if (!isIncomingCallMasterTab()) {
          return
        }

        if (isStaleIncomingCall(payload.data?.timestamp)) {
          closeIncomingCallNotification(payload.data?.callId || '')
          return
        }

        // Log the full payload to see what Firebase is actually sending
        console.log('📨 Payload Data:', JSON.stringify(payload.data, null, 2))
        
        // Prepare call data - include all fields from payload.data
        // Try multiple possible field names for callHeaderId
        const callHeaderId = 
          payload.data?.callHeaderId || 
          payload.data?.call_header_id || 
          payload.data?.headerId || 
          payload.data?.callHeader || 
          payload.data?.['call-header-id'] ||
          ''
        
        const callData = {
          callId: payload.data?.callId || '',
          action: payload.data?.action || '',
          caller: payload.data?.caller || 'Unknown Caller',
          language: payload.data?.language || 'Unknown',
          channel: payload.data?.channel || 'Unknown',
          type: payload.data?.type || 'Unknown',
          timestamp: payload.data?.timestamp || new Date().toISOString(),
          callHeaderId: callHeaderId,
          // Include any additional fields from payload.data
          ...Object.keys(payload.data || {}).reduce((acc, key) => {
            if (!['callId', 'action', 'caller', 'language', 'channel', 'type', 'timestamp', 'callHeaderId', 'call_header_id', 'headerId', 'callHeader', 'call-header-id'].includes(key)) {
              acc[key] = payload.data![key]
            }
            return acc
          }, {} as Record<string, any>),
        }

        // Show browser notification with sound for incoming calls
        // This ensures sound plays even when tab is inactive
        if (
          typeof window !== 'undefined' &&
          typeof window.Notification !== 'undefined' &&
          window.Notification.permission === 'granted'
        ) {
          try {
            const callerInfo = callData.caller ? JSON.parse(callData.caller) : null
            const callerName = callerInfo 
              ? `${callerInfo.firstName || ''} ${callerInfo.lastName || ''}`.trim() 
              : 'Unknown Caller'
            const companyName = callerInfo?.companyName || 'Unknown Company'

            const notificationKey = callData.callId || callData.callHeaderId || 'incoming-call'
            closeIncomingCallNotification(notificationKey)
            const notification = new window.Notification('Incoming Call', {
              body: `${callerName} from ${companyName}`,
              icon: '/media/logos/favicon.ico',
              badge: '/media/logos/favicon.ico',
              tag: `incoming-call-${notificationKey}`,
              requireInteraction: false,
              silent: false, // Ensure default notification sound plays
            })
            openIncomingCallNotifications.set(notificationKey, notification)

            notification.onclick = () => {
              window.focus()
              notification.close()
            }
            notification.onclose = () => {
              if (openIncomingCallNotifications.get(notificationKey) === notification) {
                openIncomingCallNotifications.delete(notificationKey)
              }
            }

            // Chrome keeps requireInteraction notifications on screen. Close this one
            // when the ring window ends so it does not linger after the call is gone.
            setTimeout(() => {
              closeIncomingCallNotification(notificationKey)
            }, INCOMING_CALL_TTL_MS)
          } catch (error) {
            console.error('Error showing browser notification:', error)
          }
        }

        // Always request service worker to play audio (works in background and foreground)
        // Service worker can play audio without user interaction, unlike main page
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          try {
            navigator.serviceWorker.controller.postMessage({
              type: 'PLAY_RINGTONE',
              callId: callData.callId || callData.callHeaderId,
            })
            console.log('📞 Sent PLAY_RINGTONE message to service worker')
          } catch (swError) {
            console.error('Error sending PLAY_RINGTONE to service worker:', swError)
          }
        } else {
          console.warn('⚠️ Service worker not available, audio may not play')
        }

        // Use global handler if available
        if (globalIncomingCallHandler) {
          globalIncomingCallHandler(callData)
        } else {
          // Fallback to toast if no handler
          const title = payload.notification?.title || 'Incoming Call'
          const body = payload.notification?.body || 'You have an incoming call'
          toast.info(`${title}: ${body}`, {
            position: 'top-center',
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
        }
      } else {
        // Show regular toast for non-call notifications
        const title = payload.notification?.title || 'New Notification'
        const body = payload.notification?.body || 'You have a new message'

        toast.info(`${title}: ${body}`, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      }
    })
  }

  /**
   * Register service worker for background messages
   */
  public async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        return registration
      } else {
        return null
      }
    } catch (error) {
      return null
    }
  }

  /**
   * Get device information for registration
   */
  public getDeviceInfo(): {
    deviceType: string
    deviceName: string
    deviceModel: string
  } {
    const userAgent = navigator.userAgent
    const platform = navigator.platform

    // Detect device type
    let deviceType = 'Desktop'
    if (/Android/i.test(userAgent)) {
      deviceType = 'Android'
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      deviceType = 'iOS'
    } else if (/Windows/i.test(userAgent)) {
      deviceType = 'Windows'
    } else if (/Mac/i.test(userAgent)) {
      deviceType = 'Mac'
    } else if (/Linux/i.test(userAgent)) {
      deviceType = 'Linux'
    }

    // Get browser name
    let deviceName = 'Unknown Browser'
    if (userAgent.includes('Chrome')) {
      deviceName = 'Chrome'
    } else if (userAgent.includes('Firefox')) {
      deviceName = 'Firefox'
    } else if (userAgent.includes('Safari')) {
      deviceName = 'Safari'
    } else if (userAgent.includes('Edge')) {
      deviceName = 'Edge'
    }

    return {
      deviceType,
      deviceName: `${deviceName} on ${platform}`,
      deviceModel: userAgent,
    }
  }

  /**
   * Initialize Firebase messaging with all setup
   */
  public async initialize(): Promise<string | null> {
    try {
      const messaging = await this.resolveMessaging()
      if (!messaging) return null

      // Register service worker first
      await this.registerServiceWorker()

      // Set up foreground message listener
      await this.setupForegroundMessageListener()

      // Request permission and get token
      const token = await this.requestPermissionAndGetToken()

      return token
    } catch (error) {
      return null
    }
  }

  /**
   * Get the current stored token
   */
  public getStoredToken(): string | null {
    return this.currentToken
  }
}

export const firebaseMessagingService = FirebaseMessagingService.getInstance()
